import type { Task } from '../db/models'

/** Abstand zwischen zwei frisch nummerierten Zeilen — grob genug, dass viele
 *  Einfügungen dazwischen passen, ohne dass Fließkomma stumpf wird. */
export const DAY_ORDER_STEP = 1000

/**
 * An welche Stelle des Zieltages die gezogene Zeile kommt.
 *
 * **Gezählt wird OHNE die gezogene Zeile** — genau so, wie
 * {@link planDayReorder} den Index versteht. Bis zum 22.09.2026 rechnete die
 * Seite ihn gegen den Tag **mit** ihr: Zog man innerhalb eines Tages eine
 * Zeile nach unten, landete sie eine Stelle zu weit hinten (bei `[X,Y,Z]` und
 * X hinter Y abgelegt kam `[Y,Z,X]` statt `[Y,X,Z]`).
 *
 * **Im selben Tag entscheiden die Plätze, nicht der Zeiger.** Sichtbar
 * verschiebt `verticalListSortingStrategy` die Zeilen allein nach Quell- und
 * Zielplatz; ein „davor/dahinter" innerhalb der Zielzeile kennt sie gar
 * nicht. Hing das Ablegen trotzdem am Zeiger, sagten Bild und Ergebnis
 * Verschiedenes — und der Zeigerwert schwankte obendrein, weil er gegen die
 * gerade ausweichende Zielzeile gemessen wurde. Je höher die Zeile
 * (Unteraufgaben), desto weiter wanderte ihre Mitte weg, bis die Richtung
 * zurückkippte: „erst beim zweiten oder dritten Mal" (Nutzer, 22.09.2026).
 *
 * `gapAfter` gilt deshalb nur für Zeilen aus einem **anderen** Tag — dort
 * gibt es keinen Quellplatz, aus dem sich die Richtung ergäbe, und die Lücke
 * steht, wo der Zeiger sie geöffnet hat. Ohne Zielzeile (leerer Tag) geht es
 * ans Ende.
 */
export function dropIndexInDay(
  movedId: string,
  dayTasks: Task[],
  overTaskId: string | null,
  gapAfter: boolean,
): number {
  const others = dayTasks.filter(t => t.id !== movedId)
  if (!overTaskId) return others.length

  const from = dayTasks.findIndex(t => t.id === movedId)
  // Über sich selbst: Die Zeile bleibt, wo sie ist. Ohne sie rückt alles
  // dahinter eine Stelle vor, `from` ist also genau ihr alter Platz.
  if (overTaskId === movedId) return from === -1 ? others.length : from

  const at = others.findIndex(t => t.id === overTaskId)
  if (at === -1) return others.length

  const to = dayTasks.findIndex(t => t.id === overTaskId)
  const ausDemselbenTag = from !== -1 && to !== -1
  const dahinter = ausDemselbenTag ? from < to : gapAfter
  return at + (dahinter ? 1 : 0)
}

/**
 * Was ein Zug innerhalb eines Tages schreiben muss.
 *
 * `dayTasks` ist der Zieltag in seiner **angezeigten** Reihenfolge (ohne die
 * gezogene Zeile). `index` ist die Stelle, an der sie landen soll: 0 = ganz
 * oben, `dayTasks.length` = ganz unten.
 *
 * Die gezogene Zeile nimmt die Mitte zwischen ihren neuen Nachbarn — ein Zug
 * schreibt also genau eine Zeile. Nur wenn ein Nachbar noch gar keine Position
 * hat (`dayOrder === null`, nie gezogen), bekommt der ganze Tag eine
 * Nummerierung: Ohne Nachbarwerte gibt es keine Mitte, in die man ablegen
 * könnte.
 */
export function planDayReorder(
  movedId: string,
  dayTasks: Task[],
  index: number,
): Array<{ id: string; dayOrder: number }> {
  const others = dayTasks.filter(t => t.id !== movedId)
  const at = Math.max(0, Math.min(others.length, index))
  const before = others[at - 1]
  const after = others[at]

  const beforeOrder = before?.dayOrder ?? null
  const afterOrder = after?.dayOrder ?? null

  // Beide Nachbarn haben eine Position (oder es gibt keinen): Mitte reicht.
  if ((before === undefined || beforeOrder !== null) && (after === undefined || afterOrder !== null)) {
    if (beforeOrder === null && afterOrder === null) return [{ id: movedId, dayOrder: 0 }]
    if (beforeOrder === null) return [{ id: movedId, dayOrder: afterOrder! - DAY_ORDER_STEP }]
    if (afterOrder === null) return [{ id: movedId, dayOrder: beforeOrder + DAY_ORDER_STEP }]
    return [{ id: movedId, dayOrder: (beforeOrder + afterOrder) / 2 }]
  }

  // Mindestens ein Nachbar ist unnummeriert: den ganzen Tag einmal durchzählen,
  // in der Reihenfolge, die gerade zu sehen ist.
  const ordered = [...others]
  ordered.splice(at, 0, { id: movedId } as Task)
  return ordered.map((task, i) => ({ id: task.id, dayOrder: i * DAY_ORDER_STEP }))
}
